import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { getApiEndpoints, getSelectedEnvironment } from '../utils/apiConfig';

const STATUS_LABELS = {
  PENDING:    { label: 'Pending',    color: '#ff9800' },
  PROCESSING: { label: 'Processing', color: '#2196f3' },
  COMPLETED:  { label: 'Completed',  color: '#4caf50' },
  PARTIAL:    { label: 'Partial',    color: '#ff9800' },
  FAILED:     { label: 'Failed',     color: '#f44336' },
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

const JOB_TYPE_LABELS = {
  AGENT_UPLOAD:  { label: 'Agent Upload',  color: '#4caf50' },
  PROGRAM_DATA:  { label: 'Program Data',  color: '#673ab7' },
  QUICK_CONNECT: { label: 'Quick Connect', color: '#00897b' },
  ROUTING_PROFILE: { label: 'Routing Profile', color: '#1976d2' },
  SECURITY_PROFILE: { label: 'Security Profile', color: '#7b1fa2' },
  USER_HIERARCHY_GROUP: { label: 'User Hierarchy Group', color: '#5d4037' },
};

const BulkJobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('ALL');

  const apiEndpoints = getApiEndpoints(getSelectedEnvironment());
  const BULK_JOBS_LIST_API = apiEndpoints.bulkJobsListApi;

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(BULK_JOBS_LIST_API);
      let data = response.data;
      if (data.body && typeof data.body === 'string') {
        try { data = JSON.parse(data.body); } catch (_) {}
      }
      if (data.status === 'ok') {
        setJobs(data.jobs || []);
      } else {
        setError(data.message || 'Failed to load jobs');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [BULK_JOBS_LIST_API]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  return (
    <div className="setup-page">
      <div className="page-header">
        <div>
          <h1>Bulk Job History</h1>
          <p className="page-subtitle">All bulk upload jobs</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button onClick={fetchJobs} className="btn btn-secondary" disabled={loading}>
            {loading ? 'Loading...' : '↻ Refresh'}
          </button>
          <Link to="/home" className="btn btn-secondary">← Back to Home</Link>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <h4>Error</h4>
          <p>{error}</p>
        </div>
      )}

      {loading && (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading jobs...</p>
        </div>
      )}

      {!loading && !error && jobs.length === 0 && (
        <div className="alert alert-info" style={{ marginTop: '1rem' }}>
          <p>No bulk upload jobs found.</p>
        </div>
      )}

      {!loading && jobs.length > 0 && (
        <>
          {/* Filter tabs */}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', marginBottom: '0.5rem' }}>
            {[
              { key: 'ALL',          label: 'All Jobs' },
              { key: 'AGENT_UPLOAD', label: 'Agent Upload' },
              { key: 'PROGRAM_DATA', label: 'Program Data' },
              { key: 'QUICK_CONNECT', label: 'Quick Connect' },
              { key: 'ROUTING_PROFILE', label: 'Routing Profile' },
              { key: 'SECURITY_PROFILE', label: 'Security Profile' },
              { key: 'USER_HIERARCHY_GROUP', label: 'User Hierarchy Group' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                style={{
                  padding: '6px 16px',
                  borderRadius: '16px',
                  border: '1.5px solid',
                  borderColor: activeFilter === f.key ? '#1976d2' : '#ccc',
                  backgroundColor: activeFilter === f.key ? '#1976d2' : '#fff',
                  color: activeFilter === f.key ? '#fff' : '#333',
                  fontWeight: activeFilter === f.key ? '600' : 'normal',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                }}
              >
                {f.label}
              </button>
            ))}
          </div>

        <div style={{ overflowX: 'auto', marginTop: '0.5rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                <th style={thStyle}>Submitted</th>
                <th style={thStyle}>Job ID</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Submitted By</th>
                <th style={thStyle}>Instance</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Total</th>
                <th style={thStyle}>✅</th>
                <th style={thStyle}>❌</th>
                <th style={thStyle}>Progress</th>
                <th style={thStyle}>Completed</th>
                <th style={thStyle}>Details</th>
              </tr>
            </thead>
            <tbody>
              {jobs
                .filter(j => activeFilter === 'ALL' || (j.job_type || 'AGENT_UPLOAD') === activeFilter)
                .map((job, idx) => {
                const statusInfo  = STATUS_LABELS[job.status] || { label: job.status, color: '#607d8b' };
                const typeInfo    = JOB_TYPE_LABELS[job.job_type || 'AGENT_UPLOAD'] || { label: job.job_type, color: '#607d8b' };
                return (
                  <tr key={job.job_id} style={{ borderBottom: '1px solid #eee', backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={tdStyle}>{formatDate(job.created_at)}</td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.78rem', maxWidth: '200px', wordBreak: 'break-all' }}>
                      {job.job_id}
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        backgroundColor: typeInfo.color,
                        color: '#fff',
                        fontWeight: '600',
                        fontSize: '0.78rem',
                      }}>
                        {typeInfo.label}
                      </span>
                    </td>
                    <td style={tdStyle}>{job.submitted_by || '—'}</td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.78rem', maxWidth: '150px', wordBreak: 'break-all' }}>
                      {(job.job_type || 'AGENT_UPLOAD') === 'PROGRAM_DATA' ? '—' : (job.instance_id || '—')}
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        backgroundColor: statusInfo.color,
                        color: '#fff',
                        fontWeight: '600',
                        fontSize: '0.78rem',
                      }}>
                        {statusInfo.label}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>{job.total}</td>
                    <td style={{ ...tdStyle, textAlign: 'center', color: '#4caf50', fontWeight: '600' }}>{job.succeeded}</td>
                    <td style={{ ...tdStyle, textAlign: 'center', color: job.failed > 0 ? '#f44336' : 'inherit', fontWeight: job.failed > 0 ? '600' : 'normal' }}>
                      {job.failed}
                    </td>
                    <td style={{ ...tdStyle, minWidth: '80px' }}>
                      <div style={{ backgroundColor: '#e0e0e0', borderRadius: '4px', height: '8px', width: '100%' }}>
                        <div style={{
                          height: '100%',
                          borderRadius: '4px',
                          width: `${job.progress_pct || 0}%`,
                          backgroundColor: job.status === 'FAILED' ? '#f44336' : job.status === 'PARTIAL' ? '#ff9800' : '#4caf50',
                        }} />
                      </div>
                      <span style={{ fontSize: '0.75rem', color: '#666' }}>{Math.round(job.progress_pct || 0)}%</span>
                    </td>
                    <td style={tdStyle}>{formatDate(job.completed_at)}</td>
                    <td style={tdStyle}>
                      <Link to={`/bulk-jobs/${job.job_id}`} className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem' }}>
                        View
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
};

const thStyle = {
  padding: '10px 12px',
  textAlign: 'left',
  fontWeight: '600',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '8px 12px',
  verticalAlign: 'middle',
};

export default BulkJobsPage;
